/**
 * 练习记忆服务 - 基于艾宾浩斯记忆曲线的练习重复系统
 * 管理练习记录的保存、检索和重复时机判断
 */

import { backendAI } from './backendAI';

export interface PracticeRecord {
  id: string;
  sessionId: string;
  chineseText: string;
  targetEnglish: string;
  userAnswer?: string;
  score?: number;
  difficulty: 'easy' | 'medium' | 'hard';
  createdAt: string;
  answeredAt?: string;
  lastReviewedAt?: string;
  nextReviewAt?: string;
  reviewCount: number;
  masteryLevel: number; // 0-100，掌握程度
}

export interface PracticeSession {
  id: string;
  userId: string;
  conversationId?: string;
  sessionDate: string;
  totalExercises: number;
  completedExercises: number;
  totalScore: number;
  averageScore: number;
  sourceMessagesCount: number;
  completedAt?: string;
  exercises: PracticeRecord[];
}

export interface MemoryCurveConfig {
  // 记忆曲线间隔（天）
  intervals: number[];
  // 根据掌握程度调整间隔的系数
  masteryMultiplier: {
    [key: number]: number; // masteryLevel -> multiplier
  };
}

export class PracticeMemoryService {
  private static instance: PracticeMemoryService;
  
  // 艾宾浩斯记忆曲线配置
  private readonly memoryCurve: MemoryCurveConfig = {
    intervals: [1, 3, 7, 15, 30, 90], // 1天, 3天, 1周, 2周, 1月, 3月
    masteryMultiplier: {
      0: 0.5,   // 完全不会，缩短间隔
      20: 0.7,  // 基础掌握
      40: 0.8,  // 一般掌握  
      60: 1.0,  // 标准掌握
      80: 1.3,  // 较好掌握
      100: 1.8, // 完全掌握，延长间隔
    }
  };

  static getInstance(): PracticeMemoryService {
    if (!PracticeMemoryService.instance) {
      PracticeMemoryService.instance = new PracticeMemoryService();
    }
    return PracticeMemoryService.instance;
  }

  /**
   * 保存练习会话记录
   */
  async savePracticeSession(
    exercises: any[],
    conversationId?: string,
    sourceMessagesCount: number = 0
  ): Promise<string> {
    try {
      const sessionData = {
        conversation_id: conversationId,
        total_exercises: exercises.length,
        completed_exercises: exercises.filter(ex => ex.userAnswer).length,
        total_score: exercises.reduce((sum, ex) => sum + (ex.score || 0), 0),
        source_messages_count: sourceMessagesCount,
        completed_at: new Date().toISOString(),
        metadata: {
          exercises: exercises.map(ex => ({
            chinese_text: ex.chineseText,
            target_english: ex.targetEnglish || ex.expectedAnswer,
            user_answer: ex.userAnswer,
            score: ex.score || 0,
            difficulty: this.calculateDifficulty(ex),
            mastery_level: this.calculateMasteryLevel(ex),
          }))
        }
      };

      const response = await backendAI.request('/practice-sessions', {
        method: 'POST',
        body: JSON.stringify(sessionData)
      });

      console.log('✅ 练习记录已保存:', response);
      return response.id;
    } catch (error) {
      console.error('❌ 保存练习记录失败:', error);
      throw error;
    }
  }

  /**
   * 获取需要复习的练习记录
   */
  async getReviewDuePractices(): Promise<PracticeRecord[]> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await backendAI.request(`/practice-exercises/review-due?date=${today}`);
      
      console.log('📚 获取到待复习练习:', response?.length || 0, '条');
      return response || [];
    } catch (error) {
      console.error('❌ 获取复习练习失败:', error);
      return [];
    }
  }

  /**
   * 获取用户的练习历史
   */
  async getPracticeHistory(limit: number = 10): Promise<PracticeSession[]> {
    try {
      const response = await backendAI.request(`/practice-sessions?limit=${limit}&order=desc`);
      
      console.log('📖 获取练习历史:', response?.length || 0, '条记录');
      return response || [];
    } catch (error) {
      console.error('❌ 获取练习历史失败:', error);
      return [];
    }
  }

  /**
   * 更新练习记录的复习状态
   */
  async updateReviewStatus(exerciseId: string, score: number, userAnswer: string): Promise<void> {
    try {
      const masteryLevel = this.calculateMasteryFromScore(score);
      const nextReviewDate = this.calculateNextReviewDate(masteryLevel);

      await backendAI.request(`/practice-exercises/${exerciseId}/review`, {
        method: 'PATCH',
        body: JSON.stringify({
          user_answer: userAnswer,
          score: score,
          last_reviewed_at: new Date().toISOString(),
          next_review_at: nextReviewDate,
          mastery_level: masteryLevel,
        })
      });

      console.log('✅ 复习状态已更新:', { exerciseId, score, masteryLevel });
    } catch (error) {
      console.error('❌ 更新复习状态失败:', error);
      throw error;
    }
  }

  /**
   * 生成复习练习会话
   */
  async generateReviewSession(): Promise<any[]> {
    try {
      const reviewDue = await this.getReviewDuePractices();
      
      if (reviewDue.length === 0) {
        console.log('🎉 暂无需要复习的内容');
        return [];
      }

      // 按掌握程度和紧急程度排序
      const sortedReviews = reviewDue
        .sort((a, b) => {
          // 优先复习掌握程度低的
          if (a.masteryLevel !== b.masteryLevel) {
            return a.masteryLevel - b.masteryLevel;
          }
          // 其次按最后复习时间排序
          return new Date(a.lastReviewedAt || a.createdAt).getTime() - 
                 new Date(b.lastReviewedAt || b.createdAt).getTime();
        })
        .slice(0, 5); // 每次最多5题复习

      console.log('🔄 生成复习会话:', sortedReviews.length, '题');
      
      return sortedReviews.map((record, index) => ({
        id: record.id,
        chineseText: record.chineseText,
        targetEnglish: record.targetEnglish,
        expectedAnswer: record.targetEnglish,
        difficulty: record.difficulty,
        points: this.calculatePoints(record.difficulty),
        isReview: true,
        originalScore: record.score,
        masteryLevel: record.masteryLevel,
        reviewCount: record.reviewCount,
      }));
    } catch (error) {
      console.error('❌ 生成复习会话失败:', error);
      return [];
    }
  }

  /**
   * 根据练习内容计算难度
   */
  private calculateDifficulty(exercise: any): 'easy' | 'medium' | 'hard' {
    const text = exercise.chineseText || exercise.expectedAnswer || '';
    const length = text.length;
    const score = exercise.score || 0;
    
    // 基于长度和得分综合判断
    if (length < 10 && score > 80) return 'easy';
    if (length > 20 || score < 60) return 'hard';
    return 'medium';
  }

  /**
   * 计算掌握程度（0-100）
   */
  private calculateMasteryLevel(exercise: any): number {
    const score = exercise.score || 0;
    const userAnswer = exercise.userAnswer || '';
    const targetAnswer = exercise.targetEnglish || exercise.expectedAnswer || '';
    
    // 基于得分和答案相似度计算掌握程度
    let mastery = Math.max(0, score);
    
    // 如果有用户答案，计算相似度加成
    if (userAnswer && targetAnswer) {
      const similarity = this.calculateSimilarity(userAnswer, targetAnswer);
      mastery = Math.min(100, mastery + similarity * 20);
    }
    
    return Math.round(mastery);
  }

  /**
   * 从得分计算掌握程度
   */
  private calculateMasteryFromScore(score: number): number {
    // 简化计算：得分直接对应掌握程度，但考虑历史表现
    return Math.min(100, Math.max(0, score));
  }

  /**
   * 计算下次复习时间
   */
  private calculateNextReviewDate(masteryLevel: number): string {
    const now = new Date();
    
    // 根据掌握程度选择基础间隔
    let intervalIndex = 0;
    if (masteryLevel >= 90) intervalIndex = 5; // 3个月
    else if (masteryLevel >= 80) intervalIndex = 4; // 1个月  
    else if (masteryLevel >= 60) intervalIndex = 3; // 2周
    else if (masteryLevel >= 40) intervalIndex = 2; // 1周
    else if (masteryLevel >= 20) intervalIndex = 1; // 3天
    else intervalIndex = 0; // 1天
    
    const baseInterval = this.memoryCurve.intervals[intervalIndex];
    
    // 根据掌握程度调整间隔
    const multiplier = this.getMasteryMultiplier(masteryLevel);
    const adjustedInterval = Math.round(baseInterval * multiplier);
    
    // 计算下次复习日期
    const nextReviewDate = new Date(now.getTime() + adjustedInterval * 24 * 60 * 60 * 1000);
    
    return nextReviewDate.toISOString();
  }

  /**
   * 获取掌握程度对应的倍数
   */
  private getMasteryMultiplier(masteryLevel: number): number {
    const keys = Object.keys(this.memoryCurve.masteryMultiplier)
      .map(Number)
      .sort((a, b) => a - b);
    
    for (let i = keys.length - 1; i >= 0; i--) {
      if (masteryLevel >= keys[i]) {
        return this.memoryCurve.masteryMultiplier[keys[i]];
      }
    }
    
    return this.memoryCurve.masteryMultiplier[0];
  }

  /**
   * 计算字符串相似度
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();
    
    if (s1 === s2) return 1;
    
    const words1 = s1.split(/\s+/);
    const words2 = s2.split(/\s+/);
    
    const commonWords = words1.filter(word => words2.includes(word));
    const totalWords = Math.max(words1.length, words2.length);
    
    return totalWords > 0 ? commonWords.length / totalWords : 0;
  }

  /**
   * 根据难度计算分数
   */
  private calculatePoints(difficulty: string): number {
    switch (difficulty) {
      case 'easy': return 10;
      case 'medium': return 15;
      case 'hard': return 25;
      default: return 15;
    }
  }
}

export const practiceMemoryService = PracticeMemoryService.getInstance();